import type { CSSProperties } from 'react';
import { INK, RIM, ICE, TEAL, WIN, LOSS, PX_FONT, ACCENT, GOLD, CASE } from '../guild/theme';

/** Minimal shape the charts need; chronological order is the caller's job. */
/** mr is null for trades imported without prices; the reach curve leaves those out */
export interface ChartTrade { r: number; mr: number | null }

const label: CSSProperties = { fontFamily: PX_FONT, fontSize: 10, letterSpacing: '0.16em', textTransform: CASE, color: TEAL };
const axis = { fontFamily: PX_FONT, fontSize: 9, fill: 'var(--pal-axis)' };
const fmtR = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(1)}R`;

function Readout({ items }: { items: [string, string, string?][] }) {
  return (
    <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 10 }}>
      {items.map(([k, v, tone]) => (
        <span key={k} style={label}>{k} <span style={{ color: tone ?? ICE, fontSize: 12 }}>{v}</span></span>
      ))}
    </div>
  );
}

/* Cumulative R with the drawdown from the running peak shaded beneath */
export function EquityCurve({ trades, height = 200, w = 600 }: { trades: ChartTrade[]; height?: number; w?: number }) {
  const W = w, H = height, P = 28;
  let eq = 0, peak = 0, maxDd = 0;
  const pts = [{ eq: 0, peak: 0 }];
  for (const t of trades) {
    eq += t.r;
    peak = Math.max(peak, eq);
    maxDd = Math.max(maxDd, peak - eq);
    pts.push({ eq, peak });
  }
  const lo = Math.min(0, ...pts.map(p => p.eq)), hi = Math.max(1, ...pts.map(p => p.peak));
  const x = (i: number) => P + (i / Math.max(1, pts.length - 1)) * (W - P * 1.5);
  const y = (v: number) => H - P + ((lo - v) / (hi - lo)) * (H - P * 1.6);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.eq).toFixed(1)}`).join(' ');
  const ddArea = pts.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.peak).toFixed(1)}`).join(' ')
    + ' ' + pts.slice().reverse().map((p, i) => `L${x(pts.length - 1 - i).toFixed(1)},${y(p.eq).toFixed(1)}`).join(' ') + ' Z';
  return (
    <div>
      <Readout items={[['Net', fmtR(eq), eq >= 0 ? WIN : LOSS], ['Peak', fmtR(peak)], ['Max drawdown', `-${maxDd.toFixed(1)}R`, LOSS]]} />
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }} shapeRendering="crispEdges">
        {[lo, 0, hi].map(v => (
          <g key={v}>
            <line x1={P} x2={W - P / 2} y1={y(v)} y2={y(v)} stroke={v === 0 ? RIM : 'var(--pal-grid)'} strokeDasharray={v === 0 ? undefined : '3 4'} />
            <text x={2} y={y(v) + 3} {...axis}>{fmtR(v)}</text>
          </g>
        ))}
        <path d={ddArea} fill="var(--pal-area-loss)" />
        <path d={line} fill="none" stroke={ACCENT} strokeWidth={2.5} shapeRendering="geometricPrecision" />
      </svg>
    </div>
  );
}

/* Where results landed, in half-R buckets */
export function OutcomeHistogram({ trades, height = 180, w = 600 }: { trades: ChartTrade[]; height?: number; w?: number }) {
  const W = w, H = height, P = 26;
  const lo = -1.5, hi = Math.max(3, Math.ceil(Math.max(...trades.map(t => t.r), 0)));
  const step = 0.5;
  const bins = Array.from({ length: Math.round((hi - lo) / step) }, (_, i) => ({ from: lo + i * step, n: 0 }));
  for (const t of trades) {
    const i = Math.min(bins.length - 1, Math.max(0, Math.floor((t.r - lo) / step)));
    bins[i].n++;
  }
  const maxN = Math.max(1, ...bins.map(b => b.n));
  const bw = (W - P * 1.5) / bins.length;
  const mode = bins.reduce((m, b) => (b.n > m.n ? b : m), bins[0]);
  return (
    <div>
      <Readout items={[['Most common', `${fmtR(mode.from)} to ${fmtR(mode.from + step)}`], ['Stopped out', String(trades.filter(t => t.r <= -0.95).length), LOSS]]} />
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }} shapeRendering="crispEdges">
        {bins.map((b, i) => {
          const h = (b.n / maxN) * (H - P * 1.8);
          const bx = P + i * bw;
          return (
            <g key={i}>
              <rect x={bx + 2} y={H - P - h} width={bw - 4} height={h} fill={b.from < 0 ? LOSS : WIN} opacity={b.from < 0 ? 0.75 : 0.85} />
              {b.n > 0 && <text x={bx + bw / 2 - 4} y={H - P - h - 4} {...axis} fill={ICE}>{b.n}</text>}
              {Number.isInteger(b.from) && <text x={bx - 6} y={H - 8} {...axis}>{b.from}R</text>}
            </g>
          );
        })}
        <line x1={P} x2={W - P / 2} y1={H - P} y2={H - P} stroke={RIM} />
      </svg>
    </div>
  );
}

/* Win/loss run as pixel blocks plus streak records */
export function Streaks({ trades, last = 48 }: { trades: ChartTrade[]; last?: number }) {
  let longestW = 0, longestL = 0, cur = 0;
  for (const t of trades) {
    const w = t.r > 0;
    cur = w ? (cur > 0 ? cur + 1 : 1) : (cur < 0 ? cur - 1 : -1);
    longestW = Math.max(longestW, cur);
    longestL = Math.max(longestL, -cur);
  }
  const recent = trades.slice(-last);
  return (
    <div>
      <Readout items={[
        ['Current', cur === 0 ? '—' : `${Math.abs(cur)} ${cur > 0 ? 'wins' : 'losses'}`, cur > 0 ? WIN : LOSS],
        ['Longest win streak', String(longestW), WIN],
        ['Longest loss streak', String(longestL), LOSS],
      ]} />
      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }} aria-label={`Last ${recent.length} trades`}>
        {recent.map((t, i) => (
          <span key={i} title={fmtR(t.r)} style={{ width: 14, height: 22, background: t.r > 0 ? WIN : LOSS, opacity: 0.35 + Math.min(1, Math.abs(t.r) / 3) * 0.65, boxShadow: `0 0 0 1px ${INK}` }} />
        ))}
      </div>
      <div style={{ ...label, fontSize: 9, marginTop: 8 }}>Last {recent.length} trades, oldest → newest · brighter = bigger R</div>
    </div>
  );
}

/* Survival curve of max run (same read as the V6 board): share of trades that ran at least X R */
/** `w` is the drawing width in viewBox units: smaller keeps the labels readable when the chart is drawn small */
export function ReachCurve({ trades, height = 230, w = 1000 }: { trades: ChartTrade[]; height?: number; w?: number }) {
  const rs = trades.flatMap(t => (t.mr === null ? [] : [t.mr])).sort((a, b) => a - b);
  if (!rs.length) return <div style={label}>{trades.length ? 'Needs trades logged with prices — imported trades carry only their result' : 'No trades yet'}</div>;
  const W = w, H = height, padL = 52, padR = w < 700 ? 92 : 130, padT = 20, padB = 30;
  const maxR = Math.max(6, rs[rs.length - 1]) * 1.05, minR = 0;
  const pw = W - padL - padR, ph = H - padT - padB;
  const x = (r: number) => padL + ((r - minR) / (maxR - minR)) * pw;
  const y = (p: number) => padT + (1 - p) * ph;
  const surv = (r: number) => rs.filter(v => v >= r).length / rs.length;
  let path = '';
  for (let i = 0; i <= 160; i++) {
    const r = minR + ((maxR - minR) * i) / 160;
    path += `${i ? 'L' : 'M'}${x(r).toFixed(1)},${y(surv(r)).toFixed(1)}`;
  }
  const area = `${path} L${x(maxR).toFixed(1)},${y(0).toFixed(1)} L${x(minR).toFixed(1)},${y(0).toFixed(1)} Z`;
  const median = rs[Math.floor(rs.length / 2)];
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
        {[0, 0.25, 0.5, 0.75, 1].map(p => (
          <g key={p}>
            <line x1={padL} x2={W - padR} y1={y(p)} y2={y(p)} stroke="var(--pal-grid)" />
            <text x={padL - 7} y={y(p) + 3.5} textAnchor="end" {...axis}>{Math.round(p * 100)}%</text>
          </g>
        ))}
        <path d={area} fill="var(--pal-area-accent)" />
        <path d={path} fill="none" stroke={ACCENT} strokeWidth={2.5} />
        {[1, 2, 3, 5].map(n => {
          const p = surv(n);
          return (
            <g key={n}>
              <line x1={x(n)} x2={x(n)} y1={padT - 4} y2={H - padB} stroke={RIM} />
              <rect x={x(n) - 4} y={y(p) - 4} width={8} height={8} fill={GOLD} stroke={INK} strokeWidth={1.5} />
              <text x={x(n) + 8} y={y(p) - 8} fontFamily={PX_FONT} fontSize={12} fill={ICE}>{Math.round(p * 100)}%</text>
              <text x={x(n)} y={H - padB + 16} textAnchor="middle" fontFamily={PX_FONT} fontSize={11} fill={ICE}>{n}R</text>
            </g>
          );
        })}
        <text x={W - padR + 14} y={padT + 12} {...axis}>median run</text>
        <text x={W - padR + 14} y={padT + 34} fontFamily={PX_FONT} fontSize={18} fill={ICE}>{median.toFixed(2)}R</text>
        <text x={W - padR + 14} y={padT + 62} {...axis}>n = {rs.length}</text>
      </svg>
    </div>
  );
}
